import DOMPurify from 'dompurify';

export const sanitizeHTML = (html) => DOMPurify.sanitize(html);

// --- 1. SMART APPSHEET & SHEETS FORMATTER ---
const formatAppSheet = (code) => {
  const BLOCK_FUNCS = ['AND', 'OR', 'IFS', 'IF', 'CONCATENATE', 'SWITCH', 'SELECT', 'FILTER', 'ORDERBY', 'SORT', 'UNIQUE', 'LIST'];

  // 🚨 AGGRESSIVE '=' STRIPPER: Removes '=' even if hidden behind invisible rich-text characters
  let clean = code.replace(/^[\s\u200B\uFEFF\xA0]*=/, '').replace(/\s+/g, ' ').trim();

  // Secondary safety net just in case there are multiple (e.g. "==")
  while (clean.startsWith('=')) {
    clean = clean.substring(1).trim();
  }

  let result = '';
  let indentLevel = 0;
  let inString = false;
  let stringChar = '';
  let currentWord = '';
  let blockFuncStack = [];

  for (let i = 0; i < clean.length; i++) {
    const char = clean[i];

    if (inString) {
      result += char;
      if (char === stringChar) inString = false;
      continue;
    }
    if (char === '"' || char === "'") {
      inString = true;
      stringChar = char;
      result += char;
      continue;
    }

    if (char.match(/[a-zA-Z0-9_]/)) {
      currentWord += char;
      result += char;
    } else {
      if (char === '(') {
        let funcName = currentWord.toUpperCase();
        let isBlock = BLOCK_FUNCS.includes(funcName);

        if (isBlock) {
          blockFuncStack.push({ name: funcName, commas: 0 });
          indentLevel++;
          result += '(\n' + '  '.repeat(indentLevel);
        } else {
          blockFuncStack.push({ name: 'INLINE', commas: 0 });
          result += '(';
        }
        currentWord = '';
      }
      else if (char === ')') {
        if (blockFuncStack.length > 0) {
          let currentBlock = blockFuncStack.pop();
          if (currentBlock.name !== 'INLINE') {
            indentLevel = Math.max(0, indentLevel - 1);
            result += '\n' + '  '.repeat(indentLevel) + ')';
          } else {
            result += ')';
          }
        } else {
          result += ')';
        }
        currentWord = '';
      }
      else if (char === ',') {
        currentWord = '';
        let currentBlock = blockFuncStack.length > 0 ? blockFuncStack[blockFuncStack.length - 1] : null;

        if (currentBlock && currentBlock.name !== 'INLINE') {
          currentBlock.commas++;
          if (currentBlock.name === 'IFS' || currentBlock.name === 'SWITCH') {
            if (currentBlock.commas % 2 === 0) {
              result += ',\n' + '  '.repeat(indentLevel);
            } else {
              result += ', ';
            }
          } else {
            result += ',\n' + '  '.repeat(indentLevel);
          }
        } else {
          result += ', ';
        }

        if (clean[i + 1] === ' ') i++;
      }
      else {
        currentWord = '';
        if (char === ' ' && result.endsWith('\n' + '  '.repeat(indentLevel))) continue;
        result += char;
      }
    }
  }

  return result.replace(/\n\s*\n/g, '\n').trim();
};

// --- 2. APPS SCRIPT / JS / TS / REACT / VITE FORMATTER ---
const formatJS = (code) => {
  let indentLevel = 0;
  const lines = code.split('\n');
  let result = [];

  for (let line of lines) {
    let trimmed = line.trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('}') || trimmed.startsWith(']')) {
      indentLevel = Math.max(0, indentLevel - 1);
    }

    result.push('  '.repeat(indentLevel) + trimmed);

    if (trimmed.endsWith('{') || trimmed.endsWith('[')) {
      indentLevel++;
    }
  }
  return result.join('\n');
};

// --- 3. BIG QUERY SQL FORMATTER ---
const formatSQL = (code) => {
  let clean = code.replace(/\s+/g, ' ').trim();
  const keywords = ['SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'ORDER BY', 'GROUP BY', 'LIMIT', 'LEFT JOIN', 'INNER JOIN', 'RIGHT JOIN', 'HAVING'];

  keywords.forEach(kw => {
    // eslint-disable-next-line
    // securecoder-ignore
    const regex = new RegExp(`\\s+(${kw})\\s+`, 'gi');
    clean = clean.replace(regex, `\n$1 `);
  });

  return clean;
};

// --- 4. JSON FORMATTER ---
const formatJSON = (code) => {
  try {
    const parsed = JSON.parse(code);
    return JSON.stringify(parsed, null, 2);
  } catch (error) {
    alert("This JSON appears to be invalid or missing quotes. Cannot format.");
    return code;
  }
};

// --- MASTER ROUTER ---
export const formatCodeBlock = (code, type) => {
  if (!code) return '';
  const lang = type.toLowerCase();

  if (['html'].includes(lang)) return sanitizeHTML(code);
  if (['appsheet', 'sheets', 'google sheets'].includes(lang)) return formatAppSheet(code);
  if (['javascript', 'apps script', 'typescript', 'react', 'vite'].includes(lang)) return formatJS(code);
  if (['big query sql', 'sql'].includes(lang)) return formatSQL(code);
  if (['json'].includes(lang)) return formatJSON(code);

  return code;
};

// 🚨 Apply the same aggressive stripper to exports and web extractions
export const minifyExpression = (code) => {
  let cleaned = code.replace(/^[\s\u200B\uFEFF\xA0]*=/, '').replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/\s*([,()])\s*/g, '$1').trim();
  while (cleaned.startsWith('=')) {
    cleaned = cleaned.substring(1).trim();
  }
  return cleaned;
};